// A rep's routes on one day of the rep detail page (WEB_PLAN.md §5.4), the pure half of RepRouteCard: which routes
// belong to the day, their stops in visiting order with the iOS status wording and colors (RouteDetailView
// DetailStopRow / DetailStopStatus, Routes+Store.swift RouteStopOutcome), planned vs GPS-driven distance
// (`trackedMeters` / `tripSummary`) and the numbered stop pins for the day's map. Routes are planned and driven on the
// phone; the console only shows them. The routes come from the viewer's replica, which for a rep holds only their own
// or unowned routes (`can_read_as`), so nothing here widens what a rep sees. No React here, so it is unit-tested in node.

import type { RouteStatus } from '@/contracts/enums';
import {
  orderedStops,
  recordDisplayName,
  type DayLogPayload,
  type RecordPayload,
  type RoutePayload,
  type RouteStopPayload,
} from '@/contracts/models';
import { parseWireDate } from '@/contracts/wireDate';
import { isSameDay } from '@/domain/calendar';
import { formatShortTime } from '@/domain/format';
import { distanceMeters, isValidCoordinate, type Coordinate } from '@/domain/geo';
import { ms, sameId } from '@/domain/util';
import type { VisitPin } from './repDay';

// MARK: - Look

/** `RouteStatus.title`. */
export const ROUTE_STATUS_TITLES: Readonly<Record<RouteStatus, string>> = {
  planned: 'Planned',
  active: 'In route',
  completed: 'Completed',
};

/** `RouteStatus.color`: planned blue, in route orange, completed green. */
export const ROUTE_STATUS_COLORS: Readonly<Record<RouteStatus, string>> = {
  planned: '#2673E6',
  active: '#F96A3C',
  completed: '#29A85C',
};

/**
 * Stop pin colors. While a route is planned or running, DetailStopRow's: skipped gray, done green, the next stop
 * orange, the rest navy. On a finished route, the trip details' outcomes (RouteStopOutcome.statusColor): an arrived
 * stop blue and one never reached ("Not visited") amber.
 */
export const STOP_COLORS = {
  skipped: '#8C919E',
  done: '#29A85C',
  next: '#F96A3C',
  pending: '#102A5E',
  arrived: '#2673E6',
  missed: '#F5B400',
} as const;

function stopColor(state: StopState, isNext: boolean, routeStatus: RouteStatus): string {
  if (state === 'skipped') return STOP_COLORS.skipped;
  if (state === 'done') return STOP_COLORS.done;
  if (routeStatus === 'completed') return state === 'arrived' ? STOP_COLORS.arrived : STOP_COLORS.missed;
  return isNext ? STOP_COLORS.next : STOP_COLORS.pending;
}

// MARK: - The day's routes

/**
 * The rep's routes for `day`: theirs and dated that day, or started or finished that day (a route planned for
 * yesterday and driven today), plus, when `day` is today, a route still running from an earlier date (the iOS Routes
 * "Today" segment keeps active routes). The running route first, then by start (or planned date).
 */
export function routesForDay(
  routes: readonly RoutePayload[],
  userId: string,
  day: Date,
  now: Date = new Date(),
): RoutePayload[] {
  const on = (wire: string | undefined) => {
    const t = ms(wire);
    return t !== undefined && isSameDay(new Date(t), day);
  };
  const sortKey = (r: RoutePayload) => ms(r.startedAt) ?? ms(r.date) ?? 0;
  return routes
    .filter(
      (r) =>
        sameId(r.ownerId, userId) &&
        (on(r.date) || on(r.startedAt) || on(r.completedAt) || (r.status === 'active' && isSameDay(day, now))),
    )
    .sort((a, b) => Number(b.status === 'active') - Number(a.status === 'active') || sortKey(a) - sortKey(b));
}

// MARK: - Stops

export type StopState = 'done' | 'skipped' | 'arrived' | 'planned' | 'pending' | 'missed';

/**
 * Where a stop stands, in DetailStopStatus's order: done (completed), skipped, arrived, planned (an optimized arrival
 * time), pending. On a finished route a stop that was never reached is "Not visited" (RouteStopOutcome).
 */
export function stopState(
  stop: Pick<RouteStopPayload, 'completedAt' | 'skipped' | 'arrivedAt' | 'plannedArrival'>,
  routeStatus: RouteStatus,
): StopState {
  if (stop.completedAt !== undefined) return 'done';
  if (stop.skipped) return 'skipped';
  if (stop.arrivedAt !== undefined) return 'arrived';
  if (routeStatus === 'completed') return 'missed';
  if (stop.plannedArrival !== undefined) return 'planned';
  return 'pending';
}

/** "Done 2:43 PM", "Skipped", "Arrived 1:10 PM", "Planned 3:00 PM", "Pending", "Not visited". */
export function stopStatusText(
  stop: Pick<RouteStopPayload, 'completedAt' | 'arrivedAt' | 'plannedArrival'>,
  state: StopState,
  locale?: string,
): string {
  const time = (wire: string | undefined) => {
    const d = parseWireDate(wire);
    return d ? ` ${formatShortTime(d, locale)}` : '';
  };
  switch (state) {
    case 'done':
      return `Done${time(stop.completedAt)}`;
    case 'skipped':
      return 'Skipped';
    case 'arrived':
      return `Arrived${time(stop.arrivedAt)}`;
    case 'planned':
      return `Planned${time(stop.plannedArrival)}`;
    case 'missed':
      return 'Not visited';
    default:
      return 'Pending';
  }
}

/** `RouteFormat.duration`: "45 min", "1 h 20 min", "2 h", "< 1 min", and "0 min" for nothing. */
export function routeDurationText(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0 min';
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

export interface RouteStopRow {
  /** The stop id. */
  id: string;
  /** 1-based position in visiting order (`stopNumber`). */
  number: number;
  /** On the pin and the row's badge: the number, or "–" for a skipped stop (as iOS). */
  label: string;
  recordId: string;
  /** The store's name; undefined when the store is not in the viewer's replica (deleted, or not theirs to see). */
  storeName?: string;
  state: StopState;
  statusText: string;
  /** Pin and badge color. */
  color: string;
  /** The first stop not yet done, while the route is not finished ("NEXT"). */
  isNext: boolean;
  /** "Arrived 1:10 PM · 30 min on site" when both times are known. */
  onSite?: string;
  notes?: string;
  /** The store's pin (valid coordinates only). */
  coordinate?: Coordinate;
}

/** The route's stops in visiting order, worded and colored like the iOS route screens. */
export function routeStopRows(
  route: Pick<RoutePayload, 'stops' | 'status'>,
  recordOf: (id: string | undefined) => Pick<RecordPayload, 'name' | 'company' | 'coordinate'> | undefined,
  locale?: string,
): RouteStopRow[] {
  const stops = orderedStops(route);
  const next = route.status === 'completed' ? undefined : stops.find((s) => s.completedAt === undefined && !s.skipped);
  return stops.map((stop, index) => {
    const state = stopState(stop, route.status);
    const isNext = next !== undefined && stop.id === next.id;
    const color = stopColor(state, isNext, route.status);
    const row: RouteStopRow = {
      id: stop.id,
      number: index + 1,
      label: state === 'skipped' ? '–' : String(index + 1),
      recordId: stop.recordId,
      state,
      statusText: stopStatusText(stop, state, locale),
      color,
      isNext,
    };
    const record = recordOf(stop.recordId);
    if (record) {
      row.storeName = recordDisplayName(record);
      const c = record.coordinate;
      if (isValidCoordinate(c)) row.coordinate = { latitude: c.latitude, longitude: c.longitude };
    }
    const arrived = parseWireDate(stop.arrivedAt);
    const done = parseWireDate(stop.completedAt);
    if (arrived && done) {
      row.onSite = `Arrived ${formatShortTime(arrived, locale)} · ${routeDurationText((done.getTime() - arrived.getTime()) / 1000)} on site`;
    }
    if (stop.notes.trim() !== '') row.notes = stop.notes.trim();
    return row;
  });
}

// MARK: - Distance and time

/**
 * `trackedMeters(for:)`: the owner's GPS trail while the route ran (started → finished, or now while it runs),
 * summed point to point. Null without an owner, a start, or two usable points. Points without a real position (the
 * (0, 0) a damaged breadcrumb decodes to) are left out; one of them would add thousands of miles.
 */
export function drivenMeters(
  route: Pick<RoutePayload, 'ownerId' | 'startedAt' | 'completedAt'>,
  dayLogs: readonly Pick<DayLogPayload, 'userId' | 'points'>[],
  now: Date = new Date(),
): number | null {
  const from = ms(route.startedAt);
  if (!route.ownerId || from === undefined) return null;
  const to = ms(route.completedAt) ?? now.getTime();
  const points: { t: number; c: Coordinate }[] = [];
  for (const log of dayLogs) {
    if (!sameId(log.userId, route.ownerId)) continue;
    for (const p of log.points) {
      const t = ms(p.timestamp);
      if (t === undefined || t < from || t > to || !isValidCoordinate(p.coordinate)) continue;
      points.push({ t, c: p.coordinate });
    }
  }
  if (points.length < 2) return null;
  points.sort((a, b) => a.t - b.t);
  let total = 0;
  for (let i = 1; i < points.length; i += 1) total += distanceMeters(points[i - 1]!.c, points[i]!.c);
  return total;
}

export interface RouteCardModel {
  id: string;
  /** The route's name, "Route" without one. */
  name: string;
  status: RouteStatus;
  statusTitle: string;
  statusColor: string;
  /** Shown as a chip until the route is finished (as the iOS hero). */
  isOptimized: boolean;
  stops: RouteStopRow[];
  /** Stops completed (skipped ones not included). */
  done: number;
  skipped: number;
  /** Completed or skipped, over all stops (the hero's "Done" percentage); 0 without stops. */
  progress: number;
  /** The distance the phone planned between the stops (`totalMeters`). */
  plannedMeters: number;
  /** From the rep's GPS trail while the route ran; null when not driven or not tracked. */
  drivenMeters: number | null;
  /** The phone's drive-time estimate for the plan. */
  estimatedSeconds: number;
  startedAt?: Date;
  completedAt?: Date;
  /** Started → finished, or → now while running; null before the start. */
  durationSeconds: number | null;
}

export interface RouteCardContext {
  recordOf: (id: string | undefined) => Pick<RecordPayload, 'name' | 'company' | 'coordinate'> | undefined;
  dayLogs: readonly Pick<DayLogPayload, 'userId' | 'points'>[];
  now: Date;
  locale?: string;
}

/** Everything the route card shows for one route. */
export function routeCardModel(route: RoutePayload, ctx: RouteCardContext): RouteCardModel {
  const stops = routeStopRows(route, ctx.recordOf, ctx.locale);
  const startedAt = parseWireDate(route.startedAt);
  const completedAt = parseWireDate(route.completedAt);
  const done = stops.filter((s) => s.state === 'done').length;
  const skipped = stops.filter((s) => s.state === 'skipped').length;
  const end = completedAt ?? (route.status === 'active' ? ctx.now : undefined);
  const model: RouteCardModel = {
    id: route.id,
    name: route.name.trim() || 'Route',
    status: route.status,
    statusTitle: ROUTE_STATUS_TITLES[route.status] ?? route.status,
    statusColor: ROUTE_STATUS_COLORS[route.status] ?? STOP_COLORS.pending,
    isOptimized: route.isOptimized && route.status !== 'completed',
    stops,
    done,
    skipped,
    progress: stops.length > 0 ? (done + skipped) / stops.length : 0,
    plannedMeters: Number.isFinite(route.totalMeters) ? Math.max(0, route.totalMeters) : 0,
    drivenMeters: drivenMeters(route, ctx.dayLogs, ctx.now),
    estimatedSeconds: Number.isFinite(route.estimatedSeconds) ? Math.max(0, route.estimatedSeconds) : 0,
    durationSeconds: startedAt && end ? Math.max(0, (end.getTime() - startedAt.getTime()) / 1000) : null,
  };
  if (startedAt) model.startedAt = startedAt;
  if (completedAt) model.completedAt = completedAt;
  return model;
}

/** "3 of 6 stops done · 1 skipped" (TripDetails' "Stops done" / "Stops skipped"). */
export function routeProgressText(model: Pick<RouteCardModel, 'done' | 'skipped' | 'stops'>): string {
  const total = model.stops.length;
  if (total === 0) return 'No stops';
  const head = `${model.done} of ${total} ${total === 1 ? 'stop' : 'stops'} done`;
  return model.skipped > 0 ? `${head} · ${model.skipped} skipped` : head;
}

/** "Started 9:02 AM · Finished 1:15 PM", "Started 9:02 AM", or null before the start. */
export function routeTimesText(model: Pick<RouteCardModel, 'startedAt' | 'completedAt'>, locale?: string): string | null {
  if (!model.startedAt) return null;
  const started = `Started ${formatShortTime(model.startedAt, locale)}`;
  return model.completedAt ? `${started} · Finished ${formatShortTime(model.completedAt, locale)}` : started;
}

// MARK: - Map

/**
 * The route's located stops as numbered pins for the rep's day map (RepTrailMap draws them like visit pins): the
 * stop number in the status color, "–" for a skipped stop. Clicking one opens its store.
 */
export function routeStopPins(stops: readonly RouteStopRow[]): VisitPin[] {
  return stops.flatMap((s) =>
    s.coordinate ? [{ id: s.id, recordId: s.recordId, coordinate: s.coordinate, color: s.color, label: s.label }] : [],
  );
}
